import { type RpcName, type RpcParams, type RpcResult, rpcContracts } from "api-contracts";
import type { IRpcResponse } from "api-contracts/rpc";
import { safeParse } from "valibot";

import { resolve } from "../di";
import type { IAuthState, IAuthService } from "./auth/types";

/** Flag to control auto-clear session on 401 */
let autoClearSession = true;

/** Disable auto-clear session (for testing) */
export function disableAutoClearSession(): void {
  autoClearSession = false;
}

/**
 * Get access token from DI auth state.
 */
function getAccessToken(): string | undefined {
  try {
    const authState = resolve<IAuthState>("authState");
    return authState.accessToken.get();
  } catch {
    // DiContainer not initialized yet (e.g., during app startup)
    return undefined;
  }
}

/**
 * Process RPC response, handling token refresh and auto-signout.
 */
async function processRpcResponse<T>(response: Response): Promise<T> {
  // Handle token refresh
  const refreshedToken = response.headers.get("x-access-token-refreshed");
  if (refreshedToken) {
    try {
      const authService = resolve<IAuthService>("authService");
      authService.signIn(refreshedToken);
    } catch {
      // DI not initialized
    }
  }

  if (!response.ok) {
    if (response.status === 401 && autoClearSession) {
      try {
        const authService = resolve<IAuthService>("authService");
        authService.signOut();
      } catch {
        // DI not initialized
      }
    }
    throw new Error("Request failed");
  }

  const data = await response.json();
  return data as T;
}

export async function rpc<T extends RpcName>(rpcName: T, params: RpcParams<T>): Promise<RpcResult<T>> {
  // Validate params against the contract schema
  const contract = rpcContracts[rpcName];

  if (!contract) {
    throw new Error(`Unknown RPC: ${rpcName}`);
  }

  const validation = safeParse(contract.paramsSchema, params);

  if (!validation.success) {
    throw new Error(`Invalid parameters: ${validation.issues.map((issue) => issue.message).join(", ")}`);
  }

  try {
    const accessToken = getAccessToken();
    const response = await fetch(`/api/rpc/${rpcName}`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(accessToken && {
          Authorization: `Bearer ${accessToken}`,
        }),
      },
      body: JSON.stringify(validation.output),
    });

    // Process response (handles token refresh and auto-signout)
    const data = await processRpcResponse<IRpcResponse<RpcResult<T>>>(response);

    if (!data.success) {
      throw new Error(data.errorMessage || "RPC call failed");
    }

    const resultValidation = safeParse(contract.resultSchema, data.data);
    if (!resultValidation.success) {
      throw new Error(`Invalid response data: ${resultValidation.issues.map((issue) => issue.message).join(", ")}`);
    }

    return data.data!;
  } catch (error) {
    if (error instanceof Error) {
      throw error;
    }
    throw new Error("Network error occurred");
  }
}
